import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, 
  ClipboardCheck, 
  Users, 
  Calendar, 
  TrendingUp, 
  AlertTriangle,
  Shield,
  MapPin,
  Building,
  BarChart3,
  RefreshCw,
  AlertOctagon,
  FileWarning,
  CheckCircle,
  XCircle,
  Cloud,
  Route
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configuration for each form type
const FORM_CONFIG = {
  hazard_id: {
    title: 'Hazard Identification Analytics',
    subtitle: 'Hazard reporting insights and trends',
    icon: AlertTriangle,
    color: 'amber',
    gradientFrom: 'from-amber-500/10',
    gradientTo: 'to-amber-600/5',
    borderColor: 'border-amber-500/20',
    textColor: 'text-amber-400',
    bgColor: 'bg-amber-500/20'
  },
  incident: {
    title: 'Incident Report Analytics',
    subtitle: 'Incident and near-miss insights',
    icon: AlertOctagon,
    color: 'red',
    gradientFrom: 'from-red-500/10',
    gradientTo: 'to-red-600/5',
    borderColor: 'border-red-500/20',
    textColor: 'text-red-400',
    bgColor: 'bg-red-500/20'
  },
  site_tour: {
    title: 'Site Tour Analytics',
    subtitle: 'Site inspection insights and compliance',
    icon: MapPin,
    color: 'blue',
    gradientFrom: 'from-blue-500/10',
    gradientTo: 'to-blue-600/5',
    borderColor: 'border-blue-500/20',
    textColor: 'text-blue-400',
    bgColor: 'bg-blue-500/20'
  }
};

export default function SafetyAnalyticsPage() {
  const { formType } = useParams();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const config = FORM_CONFIG[formType] || FORM_CONFIG.hazard_id;
  const Icon = config.icon;

  useEffect(() => {
    fetchAnalytics();
  }, [formType]);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('vaultai_token');
      const res = await axios.get(`${API}/forms/analytics/${formType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(res.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAnalytics();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No analytics data available</p>
      </div>
    );
  }

  const { summary } = analytics;

  return (
    <div className="space-y-6" data-testid={`${formType}-analytics-page`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/dashboard/forms">
            <Button variant="outline" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Icon className={`w-6 h-6 ${config.textColor}`} />
              {config.title}
            </h1>
            <p className="text-muted-foreground">{config.subtitle}</p>
          </div>
        </div>
        <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card className={`bg-gradient-to-br ${config.gradientFrom} ${config.gradientTo} ${config.borderColor}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${config.bgColor}`}>
                <ClipboardCheck className={`h-5 w-5 ${config.textColor}`} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total</p>
                <p className={`text-2xl font-bold ${config.textColor}`}>{summary.total.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/20">
                <Calendar className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Today</p>
                <p className="text-2xl font-bold text-green-400">{summary.today}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/20">
                <TrendingUp className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">This Week</p>
                <p className="text-2xl font-bold text-purple-400">{summary.this_week}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 border-cyan-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-cyan-500/20">
                <BarChart3 className="h-5 w-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">This Month</p>
                <p className="text-2xl font-bold text-cyan-400">{summary.this_month}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-pink-500/10 to-pink-600/5 border-pink-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-pink-500/20">
                <Users className="h-5 w-5 text-pink-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Reporters</p>
                <p className="text-2xl font-bold text-pink-400">{summary.unique_reporters}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-slate-500/10 to-slate-600/5 border-slate-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-500/20">
                <TrendingUp className="h-5 w-5 text-slate-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Avg/Day</p>
                <p className="text-2xl font-bold text-slate-400">{summary.avg_per_day}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Type-specific analytics */}
      {formType === 'hazard_id' && <HazardIdAnalytics analytics={analytics} />}
      {formType === 'incident' && <IncidentAnalytics analytics={analytics} />}
      {formType === 'site_tour' && <SiteTourAnalytics analytics={analytics} />}

      {/* Quick Actions */}
      <div className="flex gap-4">
        <Link to="/dashboard/forms">
          <Button variant="outline">
            <ClipboardCheck className="w-4 h-4 mr-2" />
            View All Forms
          </Button>
        </Link>
        <Link to="/dashboard/forms/submissions">
          <Button variant="outline">
            <BarChart3 className="w-4 h-4 mr-2" />
            Browse Submissions
          </Button>
        </Link>
      </div>
    </div>
  );
}

// Hazard ID specific charts
function HazardIdAnalytics({ analytics }) {
  const { hazard_sources, risk_levels, actions_taken, stop_work_pct, top_reporters } = analytics;
  const maxSourceCount = hazard_sources?.[0]?.count || 1;

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Hazard Sources */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Hazard Sources
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {hazard_sources?.slice(0, 8).map((source, idx) => (
              <div key={source.source} className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground w-4">{idx + 1}</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm">{source.source}</span>
                    <span className="text-sm font-medium text-muted-foreground">{source.count.toLocaleString()}</span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all"
                      style={{ width: `${(source.count / maxSourceCount) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Risk Levels */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-red-500" />
            Risk Levels
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {risk_levels?.slice(0, 8).map((risk) => {
              const color = risk.level?.toLowerCase() === 'serious' || risk.level?.toLowerCase() === 'fatal' 
                ? 'from-red-500 to-red-600' 
                : risk.level?.toLowerCase() === 'minor' 
                ? 'from-green-500 to-green-600'
                : 'from-amber-500 to-amber-600';
              return (
                <div key={risk.level} className="flex items-center justify-between p-3 rounded bg-secondary/30">
                  <span className="text-sm font-medium">{risk.level || 'Unknown'}</span>
                  <span className={`text-lg font-bold ${
                    risk.level?.toLowerCase() === 'serious' || risk.level?.toLowerCase() === 'fatal' 
                    ? 'text-red-400' 
                    : risk.level?.toLowerCase() === 'minor'
                    ? 'text-green-400'
                    : 'text-amber-400'
                  }`}>{risk.count.toLocaleString()}</span>
                </div>
              );
            })}
          </div>
          {stop_work_pct > 0 && (
            <div className="mt-4 p-3 rounded bg-red-500/10 border border-red-500/20">
              <div className="flex items-center justify-between">
                <span className="text-sm text-red-400">Stop Work Initiated</span>
                <span className="text-lg font-bold text-red-400">{stop_work_pct}%</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Reporters */}
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-500" />
            Top Reporters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {top_reporters?.slice(0, 10).map((reporter, idx) => (
              <div key={reporter.name} className="flex items-center justify-between p-2 rounded bg-secondary/30">
                <div className="flex items-center gap-2">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    idx === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                    idx === 1 ? 'bg-slate-400/20 text-slate-400' :
                    idx === 2 ? 'bg-amber-600/20 text-amber-500' :
                    'bg-secondary text-muted-foreground'
                  }`}>
                    {idx + 1}
                  </span>
                  <span className="text-sm truncate max-w-[100px]">{reporter.name}</span>
                </div>
                <span className="text-sm font-medium text-primary">{reporter.count}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Incident specific charts
function IncidentAnalytics({ analytics }) {
  const { incident_types, severity_levels, weather_conditions, road_conditions, injury_pct, top_clients, top_reporters } = analytics;

  return (
    <div className="space-y-6">
      {/* Key Stats Row */}
      {injury_pct > 0 && (
        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-red-500/20">
                  <FileWarning className="h-6 w-6 text-red-400" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Incidents Resulting in Injury</p>
                  <p className="text-3xl font-bold text-red-400">{injury_pct}%</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Severity Levels */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertOctagon className="h-5 w-5 text-red-500" />
              Severity Levels
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {severity_levels?.slice(0, 8).map((sev) => (
                <div key={sev.level} className="flex items-center justify-between p-3 rounded bg-secondary/30">
                  <span className="text-sm font-medium">{sev.level || 'Not Specified'}</span>
                  <span className="text-lg font-bold text-red-400">{sev.count.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Weather Conditions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Cloud className="h-5 w-5 text-blue-500" />
              Weather Conditions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {weather_conditions?.slice(0, 8).map((w) => (
                <div key={w.weather} className="flex items-center justify-between p-2 rounded bg-secondary/30">
                  <span className="text-sm">{w.weather}</span>
                  <span className="text-sm font-medium text-muted-foreground">{w.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Road Conditions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Route className="h-5 w-5 text-amber-500" />
              Road Conditions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {road_conditions?.slice(0, 8).map((r) => (
                <div key={r.condition} className="flex items-center justify-between p-2 rounded bg-secondary/30">
                  <span className="text-sm">{r.condition}</span>
                  <span className="text-sm font-medium text-muted-foreground">{r.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Clients */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building className="h-5 w-5 text-purple-500" />
              Top Clients
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {top_clients?.slice(0, 8).map((c) => (
                <div key={c.client} className="flex items-center justify-between p-2 rounded bg-secondary/30">
                  <span className="text-sm truncate max-w-[200px]">{c.client}</span>
                  <span className="text-sm font-medium text-muted-foreground">{c.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Reporters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-500" />
            Top Reporters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {top_reporters?.slice(0, 10).map((reporter, idx) => (
              <div key={reporter.name} className="flex items-center justify-between p-2 rounded bg-secondary/30">
                <div className="flex items-center gap-2">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    idx === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                    idx === 1 ? 'bg-slate-400/20 text-slate-400' :
                    idx === 2 ? 'bg-amber-600/20 text-amber-500' :
                    'bg-secondary text-muted-foreground'
                  }`}>
                    {idx + 1}
                  </span>
                  <span className="text-sm truncate max-w-[100px]">{reporter.name}</span>
                </div>
                <span className="text-sm font-medium text-primary">{reporter.count}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Site Tour specific charts
function SiteTourAnalytics({ analytics }) {
  const { top_sites, flha_compliance_pct, ppe_compliance_pct, erp_compliance_pct, top_inspectors } = analytics;
  const maxSiteCount = top_sites?.[0]?.count || 1;

  return (
    <div className="space-y-6">
      {/* Compliance Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className={`${flha_compliance_pct >= 80 ? 'bg-green-500/10 border-green-500/20' : flha_compliance_pct >= 50 ? 'bg-amber-500/10 border-amber-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
          <CardContent className="p-4 text-center">
            <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-2 ${flha_compliance_pct >= 80 ? 'bg-green-500/20' : flha_compliance_pct >= 50 ? 'bg-amber-500/20' : 'bg-red-500/20'}`}>
              {flha_compliance_pct >= 80 ? <CheckCircle className="w-6 h-6 text-green-400" /> : <XCircle className={`w-6 h-6 ${flha_compliance_pct >= 50 ? 'text-amber-400' : 'text-red-400'}`} />}
            </div>
            <p className="text-xs text-muted-foreground mb-1">FLHA Compliance</p>
            <p className={`text-3xl font-bold ${flha_compliance_pct >= 80 ? 'text-green-400' : flha_compliance_pct >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
              {flha_compliance_pct}%
            </p>
          </CardContent>
        </Card>

        <Card className={`${ppe_compliance_pct >= 80 ? 'bg-green-500/10 border-green-500/20' : ppe_compliance_pct >= 50 ? 'bg-amber-500/10 border-amber-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
          <CardContent className="p-4 text-center">
            <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-2 ${ppe_compliance_pct >= 80 ? 'bg-green-500/20' : ppe_compliance_pct >= 50 ? 'bg-amber-500/20' : 'bg-red-500/20'}`}>
              {ppe_compliance_pct >= 80 ? <CheckCircle className="w-6 h-6 text-green-400" /> : <XCircle className={`w-6 h-6 ${ppe_compliance_pct >= 50 ? 'text-amber-400' : 'text-red-400'}`} />}
            </div>
            <p className="text-xs text-muted-foreground mb-1">PPE Compliance</p>
            <p className={`text-3xl font-bold ${ppe_compliance_pct >= 80 ? 'text-green-400' : ppe_compliance_pct >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
              {ppe_compliance_pct}%
            </p>
          </CardContent>
        </Card>

        <Card className={`${erp_compliance_pct >= 80 ? 'bg-green-500/10 border-green-500/20' : erp_compliance_pct >= 50 ? 'bg-amber-500/10 border-amber-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
          <CardContent className="p-4 text-center">
            <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-2 ${erp_compliance_pct >= 80 ? 'bg-green-500/20' : erp_compliance_pct >= 50 ? 'bg-amber-500/20' : 'bg-red-500/20'}`}>
              {erp_compliance_pct >= 80 ? <CheckCircle className="w-6 h-6 text-green-400" /> : <XCircle className={`w-6 h-6 ${erp_compliance_pct >= 50 ? 'text-amber-400' : 'text-red-400'}`} />}
            </div>
            <p className="text-xs text-muted-foreground mb-1">ERP Communicated</p>
            <p className={`text-3xl font-bold ${erp_compliance_pct >= 80 ? 'text-green-400' : erp_compliance_pct >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
              {erp_compliance_pct}%
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top Sites */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-blue-500" />
              Most Visited Sites
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {top_sites?.slice(0, 8).map((site, idx) => (
                <div key={site.site} className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-4">{idx + 1}</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm truncate max-w-[200px]">{site.site}</span>
                      <span className="text-sm font-medium text-muted-foreground">{site.count.toLocaleString()}</span>
                    </div>
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full transition-all"
                        style={{ width: `${(site.count / maxSiteCount) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Inspectors */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-green-500" />
              Top Inspectors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {top_inspectors?.slice(0, 8).map((inspector, idx) => (
                <div key={inspector.name} className="flex items-center justify-between p-2 rounded bg-secondary/30">
                  <div className="flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      idx === 0 ? 'bg-yellow-500/20 text-yellow-400' :
                      idx === 1 ? 'bg-slate-400/20 text-slate-400' :
                      idx === 2 ? 'bg-amber-600/20 text-amber-500' :
                      'bg-secondary text-muted-foreground'
                    }`}>
                      {idx + 1}
                    </span>
                    <span className="text-sm truncate max-w-[150px]">{inspector.name}</span>
                  </div>
                  <span className="text-sm font-medium text-primary">{inspector.count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
